import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../contexts/CurrencyContext';
import { useDateFormat } from '../contexts/DateFormatContext';
import { transactionAPI, categoryAPI } from '../services/api';
import { Transaction, Category } from '../types';

interface SearchFilters {
  dateFrom: string;
  dateTo: string;
  categoryId: string;
  description: string;
  settlementFlag: string;
}

const TransactionList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { formatCurrency } = useCurrency();
  const { formatDate: formatDateByFormat, parseDate, getDateInputFormat } = useDateFormat();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({
    dateFrom: '',
    dateTo: '',
    categoryId: '',
    description: '',
    settlementFlag: ''
  });

  const loadTransactions = async (searchFilters?: SearchFilters) => {
    try {
      setLoading(true);
      let data;
      
      if (searchFilters && (searchFilters.dateFrom || searchFilters.dateTo || searchFilters.categoryId || searchFilters.description || searchFilters.settlementFlag)) {
        // Convert date format if provided
        const params: any = {};
        
        if (searchFilters.dateFrom) {
          const parsedDate = parseDate(searchFilters.dateFrom);
          if (parsedDate) {
            params.dateFrom = parsedDate.toISOString().split('T')[0];
          }
        }
        
        if (searchFilters.dateTo) {
          const parsedDate = parseDate(searchFilters.dateTo);
          if (parsedDate) {
            params.dateTo = parsedDate.toISOString().split('T')[0];
          }
        }
        
        if (searchFilters.categoryId) {
          params.categoryId = searchFilters.categoryId;
        }
        
        if (searchFilters.description) {
          params.description = searchFilters.description;
        }
        
        if (searchFilters.settlementFlag) {
          params.settlementFlag = searchFilters.settlementFlag;
        }
        
        data = await transactionAPI.search(params);
      } else {
        data = await transactionAPI.getAll();
      }
      
      setTransactions(data);
      setError('');
    } catch (err) {
      console.error('取引データ読み込みエラー:', err);
      setError(t('transaction:transactionLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await categoryAPI.getAll();
      setCategories(data);
    } catch (err) {
      console.error('カテゴリ読み込みエラー:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('transaction:deleteConfirm'))) {
      return;
    }

    try {
      await transactionAPI.delete(id);
      setTransactions(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.error('取引削除エラー:', err);
      setError(t('transaction:transactionDeleteError'));
    }
  };

  const formatAmount = (amount: number, type: string) => {
    const formatted = formatCurrency(amount);
    return type === 'income' ? `+${formatted}` : `-${formatted}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const locale = i18n.language === 'th' ? 'th-TH' : i18n.language === 'en' ? 'en-US' : 'ja-JP';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getSettlementFlagLabel = (flag: number | null | undefined) => {
    switch (flag) {
      case 1:
        return t('transaction:unsettled');
      case 9:
        return t('transaction:settled');
      default:
        return null;
    }
  };

  const handleSearch = () => {
    loadTransactions(filters);
  };

  const handleClearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      categoryId: '',
      description: '',
      settlementFlag: ''
    });
    loadTransactions();
  };

  const handleFilterChange = (field: keyof SearchFilters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    loadTransactions();
    loadCategories();
  }, []);

  if (loading) {
    return <div className="loading">{t('common:loading')}</div>;
  }

  return (
    <div className="transaction-list">
      <div className="list-header">
        <h2>{t('transaction:transactionList')}</h2>
        <div className="header-actions">
          <button 
            onClick={() => setShowFilters(!showFilters)} 
            className="btn btn-primary"
            style={{ marginRight: '10px' }}
          >
            {showFilters ? t('transaction:hideFilters') : t('transaction:showFilters')}
          </button>
          <button onClick={() => loadTransactions()} className="btn btn-secondary">
            {t('transaction:refresh')}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="search-filters" style={{ 
          padding: '20px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #ddd'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '15px' }}>{t('transaction:searchFilters')}</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label htmlFor="dateFrom" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                {t('transaction:dateFrom')}
              </label>
              <input
                type="text"
                id="dateFrom"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                placeholder={getDateInputFormat()}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            
            <div>
              <label htmlFor="dateTo" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                {t('transaction:dateTo')}
              </label>
              <input
                type="text"
                id="dateTo"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                placeholder={getDateInputFormat()}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            
            <div>
              <label htmlFor="categoryId" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                {t('common:category')}
              </label>
              <select
                id="categoryId"
                value={filters.categoryId}
                onChange={(e) => handleFilterChange('categoryId', e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="">{t('transaction:allCategories')}</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="settlementFlag" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                {t('transaction:settlementFlag')}
              </label>
              <select
                id="settlementFlag"
                value={filters.settlementFlag}
                onChange={(e) => handleFilterChange('settlementFlag', e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="">{t('transaction:allSettlement')}</option>
                <option value="1">{t('transaction:unsettled')}</option>
                <option value="9">{t('transaction:settled')}</option>
                <option value="0">{t('transaction:normal')}</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="description" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                {t('common:description')}
              </label>
              <input
                type="text"
                id="description"
                value={filters.description}
                onChange={(e) => handleFilterChange('description', e.target.value)}
                placeholder={t('transaction:searchByDescription')}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={handleSearch}
              className="btn btn-primary"
              style={{ padding: '10px 20px' }}
            >
              {t('transaction:search')}
            </button>
            <button 
              onClick={handleClearFilters}
              className="btn btn-secondary"
              style={{ padding: '10px 20px' }}
            >
              {t('transaction:clearFilters')}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="message error">
          {error}
        </div>
      )}

      {transactions.length === 0 ? (
        <div className="empty-state">
          <p>{t('transaction:noTransactions')}</p>
          <p>{t('transaction:addTransactionPrompt')}</p>
        </div>
      ) : (
        <div className="transactions">
          {transactions.map(transaction => (
            <div key={transaction.id} className="transaction-item">
              <div className="transaction-info">
                <div className="transaction-header">
                  <span className="transaction-category">
                    {transaction.category}
                  </span>
                  <span className="transaction-date">
                    {formatDate(transaction.date)}
                  </span>
                  {transaction.settlementFlag && (
                    <span className={`settlement-flag settlement-${transaction.settlementFlag}`}>
                      {getSettlementFlagLabel(transaction.settlementFlag)}
                    </span>
                  )}
                </div>
                <div className={`transaction-amount ${transaction.type}`}>
                  {formatAmount(transaction.amount, transaction.type)}
                </div>
                {transaction.description && (
                  <div className="transaction-description">
                    {transaction.description}
                  </div>
                )}
              </div>
              <div className="transaction-actions">
                <button
                  onClick={() => handleDelete(transaction.id)}
                  className="delete-btn"
                  title={t('common:delete')}
                >
                  {t('common:delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TransactionList;